/**
 * A2A Service for GrammarSpeak
 * 
 * Service wrapper for A2A GrammarSpeak capabilities
 */

import grammarSpeakClient from '../a2aClient';

class A2AGrammarSpeakService {
  constructor() {
    this.client = grammarSpeakClient;
  }

  /**
   * Get grammar groups (categories)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of grammar groups
   */
  async getGrammarGroups(params = {}) {
    console.log('[A2A Service] getGrammarGroups: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_grammar_groups', params);
      
      console.log('[A2A Service] getGrammarGroups: Success', {
        hasResult: !!result,
        hasGroups: !!(result?.groups),
        groupsCount: result?.groups?.length || 0,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get grammar groups');
      }
      
      return result.groups || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getGrammarGroups: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get grammar topics (90+ structures)
   * @param {Object} params - Query parameters (category, level, page, limit)
   * @returns {Promise<Array>} List of grammar topics
   */
  async getGrammarTopics(params = {}) {
    console.log('[A2A Service] getGrammarTopics: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_grammar_topics', params);
      
      console.log('[A2A Service] getGrammarTopics: Success', {
        hasResult: !!result,
        hasTopics: !!(result?.topics),
        topicsCount: result?.topics?.length || 0,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get grammar topics');
      }
      
      return result.topics || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getGrammarTopics: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get grammar units (13 units)
   * @param {Object} params - Query parameters (book_id)
   * @returns {Promise<Array>} List of grammar units
   */
  async getGrammarUnits(params = {}) {
    console.log('[A2A Service] getGrammarUnits: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_grammar_units', params);
      
      console.log('[A2A Service] getGrammarUnits: Success', {
        hasResult: !!result,
        hasUnits: !!(result?.units),
        unitsCount: result?.units?.length || 0,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get grammar units');
      }
      
      return result.units || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getGrammarUnits: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get learning paths
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of learning paths
   */
  async getLearningPaths(params = {}) {
    console.log('[A2A Service] getLearningPaths: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_learning_paths', params);
      
      console.log('[A2A Service] getLearningPaths: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get learning paths');
      }
      
      return result.paths || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getLearningPaths: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get roleplay scenarios (Game Mode)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of roleplay scenarios
   */
  async getRoleplayScenarios(params = {}) {
    console.log('[A2A Service] getRoleplayScenarios: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_roleplay_scenarios', params);
      
      console.log('[A2A Service] getRoleplayScenarios: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get roleplay scenarios');
      }
      
      return result.scenarios || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getRoleplayScenarios: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get daily challenges
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of daily challenges
   */
  async getDailyChallenges(params = {}) {
    console.log('[A2A Service] getDailyChallenges: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_daily_challenges', params);
      
      console.log('[A2A Service] getDailyChallenges: Success', {
        hasResult: !!result,
        hasChallenges: !!(result?.challenges),
        challengesCount: result?.challenges?.length || 0,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get daily challenges');
      }
      
      return result.challenges || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getDailyChallenges: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Complete a challenge (with XP system)
   * @param {Object} challengeData - Challenge completion data
   * @returns {Promise<Object>} Completion result with XP
   */
  async completeChallenge(challengeData) {
    console.log('[A2A Service] completeChallenge: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      hasChallengeData: !!challengeData
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('complete_challenge', challengeData);
      
      console.log('[A2A Service] completeChallenge: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to complete challenge');
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] completeChallenge: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Chat with AI Learning Assistant
   * @param {Object} params - Chat parameters (user_id, message, context)
   * @returns {Promise<Object>} AI response
   */
  async chatAIAssistant(params = {}) {
    console.log('[A2A Service] chatAIAssistant: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      hasMessage: !!params.message
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('chat_ai_assistant', params);
      
      console.log('[A2A Service] chatAIAssistant: Success', {
        hasResult: !!result,
        hasResponse: !!(result?.response),
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to chat with AI assistant');
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] chatAIAssistant: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get tutor voices
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of tutor voices
   */
  async getTutorVoices(params = {}) {
    console.log('[A2A Service] getTutorVoices: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_tutor_voices', params);
      
      console.log('[A2A Service] getTutorVoices: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get tutor voices');
      }
      
      return result.voices || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getTutorVoices: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get bilingual tutor sessions
   * @param {Object} params - Query parameters (user_id)
   * @returns {Promise<Array>} List of bilingual tutor sessions
   */
  async getBilingualTutorSessions(params = {}) {
    console.log('[A2A Service] getBilingualTutorSessions: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_bilingual_tutor_sessions', params);
      
      console.log('[A2A Service] getBilingualTutorSessions: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get bilingual tutor sessions');
      }
      
      return result.sessions || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getBilingualTutorSessions: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get tone learning units
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of tone learning units
   */
  async getToneLearningUnits(params = {}) {
    console.log('[A2A Service] getToneLearningUnits: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_tone_learning_units', params);
      
      console.log('[A2A Service] getToneLearningUnits: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get tone learning units');
      }
      
      return result.units || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getToneLearningUnits: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get user achievements
   * @param {Object} params - Query parameters (user_id)
   * @returns {Promise<Object>} User achievements
   */
  async getAchievements(params = {}) {
    console.log('[A2A Service] getAchievements: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_achievements', params);
      
      console.log('[A2A Service] getAchievements: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get achievements');
      }
      
      return result.achievements || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getAchievements: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get learning plan
   * @param {Object} params - Query parameters (user_id)
   * @returns {Promise<Object>} Learning plan
   */
  async getLearningPlan(params = {}) {
    console.log('[A2A Service] getLearningPlan: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_learning_plan', params);
      
      console.log('[A2A Service] getLearningPlan: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get learning plan');
      }
      
      return result.plan || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getLearningPlan: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get progress analysis
   * @param {Object} params - Query parameters (user_id)
   * @returns {Promise<Object>} Progress analysis
   */
  async getProgressAnalysis(params = {}) {
    console.log('[A2A Service] getProgressAnalysis: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_progress_analysis', params);
      
      console.log('[A2A Service] getProgressAnalysis: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get progress analysis');
      }
      
      return result.analysis || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getProgressAnalysis: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get grammar exercises
   * @param {Object} params - Query parameters (topic_id)
   * @returns {Promise<Array>} List of grammar exercises
   */
  async getGrammarExercises(params = {}) {
    console.log('[A2A Service] getGrammarExercises: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_grammar_exercises', params);
      
      console.log('[A2A Service] getGrammarExercises: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get grammar exercises');
      }
      
      return result.exercises || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getGrammarExercises: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Submit grammar exercise answer
   * @param {Object} exerciseData - Exercise submission data
   * @returns {Promise<Object>} Submission result
   */
  async submitGrammarExercise(exerciseData) {
    console.log('[A2A Service] submitGrammarExercise: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      hasExerciseData: !!exerciseData
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('submit_grammar_exercise', exerciseData);
      
      console.log('[A2A Service] submitGrammarExercise: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to submit grammar exercise');
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] submitGrammarExercise: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Detect user context
   * @returns {Object} User context
   */
  detectContext() {
    return this.client.detectContext();
  }

  /**
   * Negotiate UX format
   * @param {Object} context - User context
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    const userContext = context || this.detectContext();
    return await this.client.negotiateUX(userContext);
  }
}

// Create singleton instance
const a2aGrammarSpeakService = new A2AGrammarSpeakService();

export default a2aGrammarSpeakService;

